import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { achievements } from '../data/achievements';
import useCurrentUser from './useCurrentUser';

export default function useAchievements() {
    const currentUser = useCurrentUser();
    const [unlockedCodes, setUnlockedCodes] = useState([]);
    const [newlyUnlocked, setNewlyUnlocked] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadUnlocked();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadUnlocked = async () => {
        try {
            const [usersRes, achievementsRes] = await Promise.all([
                api.get('/users'),
                api.get('/user_achievements'),
            ]);

            const me = (usersRes.data.member || []).find(u => u.email === currentUser?.email);
            if (!me) {
                setUnlockedCodes([]);
                return;
            }

            const mine = (achievementsRes.data.member || []).filter(a => a.user === me['@id']);
            setUnlockedCodes(mine.map(a => a.achievementCode));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const checkAndUnlock = useCallback(async (code) => {
        if (unlockedCodes.includes(code)) return;

        try {
            const res = await api.post('/achievements/unlock', { code });
            if (res.data.unlocked) {
                setUnlockedCodes(prev => [...prev, code]);
                const achievement = achievements.find(a => a.code === code);
                if (achievement) {
                    setNewlyUnlocked(achievement);
                }
            }
        } catch (err) {
            console.error(err);
        }
    }, [unlockedCodes]);

    const checkThresholds = useCallback(async () => {
        try {
            const res = await api.get('/achievements/stats');
            const { totalPrepared, totalImmuno, nbOptimisations, ancienneteJours, semaineParfaite } = res.data;

            if (totalPrepared >= 100) checkAndUnlock('habitue_route');
            if (totalPrepared >= 1000) checkAndUnlock('roi_route');
            if (totalImmuno >= 50) checkAndUnlock('specialiste_immuno');
            if (nbOptimisations >= 10) checkAndUnlock('strategiste');
            if (ancienneteJours >= 365) checkAndUnlock('pilier_equipe');
            if (semaineParfaite) checkAndUnlock('semaine_parfaite');
        } catch (err) {
            console.error(err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dismissNotification = () => setNewlyUnlocked(null);

    return { unlockedCodes, newlyUnlocked, checkAndUnlock, checkThresholds, dismissNotification, loading };
}